<script setup lang="ts">
  import { computed, onMounted } from 'vue';
  import { useRoute } from 'vue-router';
  import { Loader2 } from 'lucide-vue-next';
  import { Separator } from '@/shared/ui';
  import RecordConfigHeader from './record-config/RecordConfigHeader.vue';
  import RecordConfigStagesSection from './record-config/RecordConfigStagesSection.vue';
  import RecordConfigProceduresSection from './record-config/RecordConfigProceduresSection.vue';
  import RecordConfigFormatSection from './record-config/RecordConfigFormatSection.vue';
  import RecordConfigStorageSection from './record-config/RecordConfigStorageSection.vue';
  import RecordConfigProtectionMeanSection from './record-config/RecordConfigProtectionMeanSection.vue';
  import RecordConfigArchivingSection from './record-config/RecordConfigArchivingSection.vue';
  import RecordConfigElectronicArchivingBlock from './record-config/RecordConfigElectronicArchivingBlock.vue';
  import RecordConfigIndexingSection from './record-config/RecordConfigIndexingSection.vue';
  import RecordConfigRetentionSection from './record-config/RecordConfigRetentionSection.vue';
  import RecordConfigDisposalSection from './record-config/RecordConfigDisposalSection.vue';
  import RecordConfigSubjectsSection from './record-config/RecordConfigSubjectsSection.vue';
  import RecordConfigFooter from './record-config/RecordConfigFooter.vue';
  import { useMatrixFields } from '../model/useMatrixFields';
  import { useRecordConfig } from '../model/useRecordConfig';
  import { useRecordConfigOptions } from '../model/useRecordConfigOptions';
  import { useRecordConfigSubjects } from '../model/useRecordConfigSubjects';

  const route = useRoute();
  const matrixId = computed(() => Number(route.params.id));

  const {
    matrix,
    form,
    isLoading,
    isSaving,
    loadFailed,
    isGdOrigin,
    isErpOrigin,
    isIfApiType,
    showsIndexing,
    functionalInspectionTypeLabel,
    canSave,
    saveBlockedReason,
    showsElectronicBlock,
    showsPhysicalBlock,
    showsArchivingPermission,
    load,
    save,
    cancel,
  } = useRecordConfig(matrixId);

  const options = useRecordConfigOptions();
  const subjects = useRecordConfigSubjects();
  const matrixFields = useMatrixFields(matrixId);

  const hasFormat = computed(() => form.format !== null);

  onMounted(async () => {
    options.load();
    subjects.load();
    await load();
    if (matrix.value) {
      options.loadForOrigin({ id: matrix.value.recordOrigin.id, isGd: isGdOrigin.value });
    }
    if (isGdOrigin.value) await matrixFields.load();
  });
</script>

<template>
  <div class="flex h-full flex-col">
    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center"
      data-testid="record-config-loading"
    >
      <Loader2 class="h-6 w-6 animate-spin text-[var(--theme-secondary-ink)]" />
    </div>

    <div
      v-else-if="!loadFailed && matrix"
      class="min-h-0 flex-1 overflow-auto"
      data-testid="record-config-content"
    >
      <h1 class="px-8 py-6 text-2xl font-bold text-gray-07">Configurar Registro</h1>

      <Separator />

      <RecordConfigHeader
        :matrix="matrix"
        :title="form.title"
        :functional-inspection-type-label="functionalInspectionTypeLabel"
        @update:title="form.title = $event"
      />

      <Separator />

      <RecordConfigStagesSection
        :has-stages="form.hasStages"
        :stage-ids="form.stageIds"
        :stages="options.stages.value"
        :is-loading="options.isLoading.value"
        @update:has-stages="form.hasStages = $event"
        @update:stage-ids="form.stageIds = $event"
      />

      <Separator />

      <RecordConfigProceduresSection
        v-model="form.procedureRevisionIds"
        :procedures="options.procedures.value"
        :is-loading="options.isLoading.value"
      />

      <Separator />

      <RecordConfigFormatSection v-model="form.format" />

      <Separator />

      <template v-if="isGdOrigin && hasFormat">
        <RecordConfigStorageSection
          :show-electronic-block="showsElectronicBlock"
          :show-physical-block="showsPhysicalBlock"
          :physical-location-ids="form.physicalLocationIds"
          :locations="options.physicalLocations.value"
          :is-loading="options.isLoading.value"
          @update:physical-location-ids="form.physicalLocationIds = $event"
        />

        <Separator />
      </template>
      <template v-else-if="isErpOrigin">
        <RecordConfigProtectionMeanSection
          v-model="form.protectionMeanIds"
          :protection-means="options.protectionMeans.value"
          :is-loading="options.isLoading.value"
        />

        <Separator />
      </template>

      <template v-if="isGdOrigin && showsElectronicBlock">
        <RecordConfigArchivingSection :show-electronic-block="showsElectronicBlock">
          <template #electronic>
            <RecordConfigElectronicArchivingBlock
              :fields="matrixFields.fields.value"
              :fields-failed="matrixFields.failed.value"
              :electronic-archiving-model="form.electronicArchivingModel"
              :archiving-routes="form.archivingRoutes"
              @update:electronic-archiving-model="form.electronicArchivingModel = $event"
              @update:archiving-routes="form.archivingRoutes = $event"
            />
          </template>
        </RecordConfigArchivingSection>

        <Separator />
      </template>

      <template v-if="showsIndexing">
        <RecordConfigIndexingSection
          v-model="form.indexing"
          :field-names="form.indexingFieldNames"
          :sequence-start="form.indexingSequenceStart"
          :indexer-field-name="form.indexerFieldName"
          :fields="matrixFields.fields.value"
          :is-loading-fields="matrixFields.isLoading.value"
          :fields-failed="matrixFields.failed.value"
          :is-if-api-type="isIfApiType"
          :locked="matrix?.indexingLocked ?? false"
          @update:field-names="form.indexingFieldNames = $event"
          @update:sequence-start="form.indexingSequenceStart = $event"
          @update:indexer-field-name="form.indexerFieldName = $event"
        />

        <Separator />
      </template>

      <RecordConfigRetentionSection
        :retention="form.retention"
        :retention-months="form.retentionMonths"
        :retention-periods="form.retentionPeriods"
        :is-erp-origin="isErpOrigin"
        @update:retention="form.retention = $event"
        @update:retention-months="form.retentionMonths = $event"
        @update:retention-periods="form.retentionPeriods = $event"
      />

      <Separator />

      <RecordConfigDisposalSection v-model="form.disposal" />

      <Separator />

      <template v-if="showsArchivingPermission">
        <RecordConfigSubjectsSection
          v-model="form.archivingPermission"
          title="Permissão para arquivamento:"
          helper-text="Selecione os Cargos e Atividades Funcionais que poderão realizar o arquivamento do registro"
          radio-name="record-config-archiving-permission"
          :automatic-only="isErpOrigin"
          :positions="subjects.positions.value"
          :functional-activities="subjects.functionalActivities.value"
          :is-loading="subjects.isLoading.value"
          :has-error="subjects.hasError.value"
        />

        <Separator />
      </template>

      <RecordConfigSubjectsSection
        v-model="form.notification"
        title="Envio de notificação:"
        helper-text="Selecione os Cargos e Atividades Funcionais que deverão receber e-mail de registros gerados"
        radio-name="record-config-notification"
        :automatic-only="false"
        :positions="subjects.positions.value"
        :functional-activities="subjects.functionalActivities.value"
        :is-loading="subjects.isLoading.value"
        :has-error="subjects.hasError.value"
      />

      <Separator />

      <RecordConfigFooter
        :can-save="canSave"
        :is-saving="isSaving"
        :blocked-reason="saveBlockedReason"
        @cancel="cancel"
        @save="save"
      />
    </div>
  </div>
</template>
